/**
 * Custom policy templates.
 *
 * Authoring screen for an organisation's OWN templates — the ones that appear,
 * marked "custom", in Policies → Create → "from template". System templates
 * are shared platform content and never appear here: the server only ever
 * returns (and only ever lets you change) templates that belong to the
 * signed-in organisation.
 *
 * THE EDITOR IS THE POLICY EDITOR
 * The body is written in the very same `RichTextEditor` (toolbar, paste
 * cleaning, supported-subset rules) that PolicyEditor.tsx uses for policies, so
 * a template is authored exactly like the policy it will become. Saving runs the
 * same client-side clean-up the policy editor does; the server sanitises again.
 *
 * Placeholders such as {{organization.name}} are substituted when a policy is
 * generated from the template.
 *
 * DELETE IS REVERSIBLE
 * Deleting deactivates the template (it leaves the picker; policies already
 * created from it are untouched). A deactivated template stays in the list and
 * can be switched back on from its edit form.
 *
 * Capability `template.manage`; the server re-checks every request.
 */

import { useCallback, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LANGUAGES } from '@opencomply/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { LanguageTag, useLanguageName } from '../components/LanguageTag';
import { Button, Card, ErrorNotice, Field, Modal, Pill, Skeleton } from '../components/ui';
import {
  RichTextEditor, POLICY_EDITOR_STRINGS, MAX_CONTENT_CHARS,
  sanitizeToElement, isEffectivelyEmpty,
  type EditorStatus, type PKey as EditorKey,
} from './PolicyEditor';

export const CUSTOM_TEMPLATES_STRINGS = {
  'templates.title': 'Custom templates',
  'templates.subtitle': 'Policy templates that belong to your organisation. They appear next to the system templates when you create a policy.',
  'templates.new': 'New template',
  'templates.empty': 'You have no custom templates yet.',
  'templates.emptyHint': 'Create one to give your team a reusable starting point for a policy.',
  'templates.loadFailed': 'Could not load your templates.',
  'templates.colName': 'Name',
  'templates.colLanguage': 'Language',
  'templates.colFrameworks': 'Frameworks',
  'templates.colStatus': 'Status',
  'templates.active': 'Active',
  'templates.inactive': 'Deleted',
  'templates.noFrameworks': 'None',
  'templates.edit': 'Edit',
  'templates.delete': 'Delete',
  'templates.newTitle': 'New template',
  'templates.editTitle': 'Edit template',
  'templates.back': 'Back to templates',
  'templates.fieldName': 'Name',
  'templates.fieldDescription': 'Description',
  'templates.fieldLanguage': 'Language',
  'templates.fieldLanguageHint': 'The language the template is written in. Policies created from it are written in this language.',
  'templates.fieldFrameworks': 'Frameworks',
  'templates.fieldFrameworksHint': 'Optional. Pick the frameworks this template helps you meet. Only frameworks your organisation has licensed are offered.',
  'templates.noLicensedFrameworks': 'Your organisation has no licensed frameworks yet.',
  'templates.fieldContent': 'Content',
  'templates.fieldContentHint': 'Use {{organization.name}} and other placeholders; they are filled in when a policy is created.',
  'templates.fieldActive': 'Active (offered when creating a policy)',
  'templates.save': 'Save template',
  'templates.saving': 'Saving…',
  'templates.cancel': 'Cancel',
  'templates.loadingOne': 'Loading template…',
  'templates.saved': 'Template saved.',
  'templates.deleted': 'Template deleted. It is no longer offered when creating a policy.',
  'templates.nameLength': 'The name must be between 3 and 255 characters.',
  'templates.descriptionRequired': 'Add a short description (up to 1000 characters).',
  'templates.contentEmpty': 'The template is empty.',
  'templates.contentTooLong': 'The template is too long to save ({{count}} of {{max}} characters).',
  'templates.removedNotice': '{{count}} unsupported items were removed so that what you see is exactly what is saved.',
  'templates.deleteTitle': 'Delete this template?',
  'templates.deleteBody': '“{{name}}” will no longer be offered when creating a policy. Policies already created from it are not affected, and you can switch it back on by editing it.',
  'templates.deleteConfirm': 'Delete template',
  'templates.dismiss': 'Dismiss',
} as const;

type TKey = keyof typeof CUSTOM_TEMPLATES_STRINGS;

interface TemplateFramework { id: string; key: string; name: string }
interface CustomTemplate {
  id: string; name: string; description: string; language: string; isActive: boolean;
  frameworks: TemplateFramework[];
  contentHtml?: string;
}
interface FrameworkRow { id: string; key: string; name: string; licensed: boolean }

type View = { kind: 'list' } | { kind: 'edit'; id: string | null };

export function CustomTemplatesPage() {
  const { allows } = useAuth();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [notice, setNotice] = useState<string | null>(null);
  const { t } = useTranslation();
  const tx = useCallback(
    (key: TKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: CUSTOM_TEMPLATES_STRINGS[key], ...options }),
    [t],
  );

  if (!allows('template.manage')) return null;

  return view.kind === 'list' ? (
    <TemplateList
      tx={tx}
      notice={notice}
      onDismiss={() => setNotice(null)}
      onNotice={setNotice}
      onEdit={(id) => { setNotice(null); setView({ kind: 'edit', id }); }}
    />
  ) : (
    <TemplateEditor
      key={view.id ?? 'new'}
      id={view.id}
      tx={tx}
      onDone={(message) => { setNotice(message); setView({ kind: 'list' }); }}
      onCancel={() => setView({ kind: 'list' })}
    />
  );
}

type Tx = (key: TKey, options?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
//  List
// ---------------------------------------------------------------------------

function TemplateList({ tx, notice, onDismiss, onNotice, onEdit }: {
  tx: Tx;
  notice: string | null;
  onDismiss: () => void;
  onNotice: (message: string) => void;
  onEdit: (id: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const [toDelete, setToDelete] = useState<CustomTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['custom-templates'],
    queryFn: () => api.get<{ items: CustomTemplate[] }>('/policies/templates/custom'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/policies/templates/${id}`),
    onSuccess: async () => {
      setToDelete(null);
      onNotice(tx('templates.deleted'));
      await queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
      void queryClient.invalidateQueries({ queryKey: ['policy-templates'] });
    },
    onError: (err) => { setToDelete(null); setError(describeError(err)); },
  });

  const items = data?.items ?? [];

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1">{tx('templates.title')}</h1>
          <p className="oc-muted mt-1">{tx('templates.subtitle')}</p>
        </div>
        <Button variant="primary" onClick={() => onEdit(null)}>{tx('templates.new')}</Button>
      </header>

      {notice && (
        <div role="status" className="rounded-xl border border-good/30 bg-good/[0.07] px-4 py-3 text-sm text-ink flex flex-wrap items-start justify-between gap-2">
          <span className="min-w-0 flex-1">{notice}</span>
          <button className="text-xs font-medium text-brand hover:underline" onClick={onDismiss}>
            {tx('templates.dismiss')}
          </button>
        </div>
      )}
      {error && <ErrorNotice message={error} />}

      {isLoading ? (
        <div className="space-y-2"><Skeleton className="h-14" /><Skeleton className="h-14" /></div>
      ) : isError ? (
        <ErrorNotice message={tx('templates.loadFailed')} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <Card className="text-center py-10">
          <p className="font-medium text-ink">{tx('templates.empty')}</p>
          <p className="oc-muted mt-1">{tx('templates.emptyHint')}</p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((tpl) => (
            <li key={tpl.id}>
              <Card className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink break-words">{tpl.name}</span>
                    <LanguageTag code={tpl.language} />
                    <Pill tone={tpl.isActive ? 'good' : 'neutral'}>
                      {tpl.isActive ? tx('templates.active') : tx('templates.inactive')}
                    </Pill>
                  </div>
                  <p className="oc-muted mt-1 line-clamp-2">{tpl.description}</p>
                  <p className="text-xs text-ink-muted mt-1">
                    {tx('templates.colFrameworks')}:{' '}
                    {tpl.frameworks.length === 0
                      ? tx('templates.noFrameworks')
                      : tpl.frameworks.map((f) => f.name).join(', ')}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={() => onEdit(tpl.id)}>{tx('templates.edit')}</Button>
                  {tpl.isActive && (
                    <Button size="sm" variant="ghost" onClick={() => { setError(null); setToDelete(tpl); }}>
                      {tx('templates.delete')}
                    </Button>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        title={tx('templates.deleteTitle')}
        footer={
          <>
            <Button onClick={() => setToDelete(null)}>{tx('templates.cancel')}</Button>
            <Button variant="danger" disabled={remove.isPending} onClick={() => toDelete && remove.mutate(toDelete.id)}>
              {tx('templates.deleteConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-secondary">{tx('templates.deleteBody', { name: toDelete?.name ?? '' })}</p>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Editor form
// ---------------------------------------------------------------------------

function TemplateEditor({ id, tx, onDone, onCancel }: {
  id: string | null;
  tx: Tx;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const languageName = useLanguageName();

  // The shared editor takes the policy editor's own translator.
  const tp = useCallback(
    (key: EditorKey, options?: Record<string, unknown>) =>
      t(key, { defaultValue: POLICY_EDITOR_STRINGS[key], ...options }),
    [t],
  );

  const editorRef = useRef<HTMLDivElement | null>(null);
  const [, setStatus] = useState<EditorStatus>({ changed: false, empty: true, length: 0 });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [language, setLanguage] = useState('en');
  const [frameworkIds, setFrameworkIds] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(id === null);

  const existing = useQuery({
    queryKey: ['custom-template', id],
    queryFn: () => api.get<CustomTemplate>(`/policies/templates/${id}`),
    enabled: id !== null,
    gcTime: 0,
  });

  // Seed the form once the template has loaded (the editor itself reads its
  // initial HTML on mount, so it is only rendered after this).
  if (!hydrated && existing.data) {
    setName(existing.data.name);
    setDescription(existing.data.description);
    setLanguage(existing.data.language);
    setFrameworkIds(existing.data.frameworks.map((f) => f.id));
    setIsActive(existing.data.isActive);
    setHydrated(true);
  }

  const frameworks = useQuery({
    queryKey: ['frameworks'],
    queryFn: () => api.get<FrameworkRow[]>('/frameworks'),
  });

  // Licensed frameworks, plus any already linked, so saving never drops a link
  // the user cannot see.
  const linked = new Map((existing.data?.frameworks ?? []).map((f) => [f.id, f.name]));
  const options = (frameworks.data ?? [])
    .filter((f) => f.licensed || linked.has(f.id))
    .map((f) => ({ id: f.id, name: f.name }));

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      id ? api.put<CustomTemplate>(`/policies/templates/${id}`, body)
         : api.post<CustomTemplate>('/policies/templates', body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
      // The "Create policy → from template" picker reads this key.
      void queryClient.invalidateQueries({ queryKey: ['policy-templates'] });
      onDone(tx('templates.saved'));
    },
    onError: (err) => setFormError(describeError(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const el = editorRef.current;
    if (!el) return;

    const trimmedName = name.trim();
    if (trimmedName.length < 3 || trimmedName.length > 255) { setFormError(tx('templates.nameLength')); return; }
    const trimmedDescription = description.trim();
    if (trimmedDescription.length < 1 || trimmedDescription.length > 1000) { setFormError(tx('templates.descriptionRequired')); return; }

    // Same clean-up the policy editor performs before saving.
    const { root, removed } = sanitizeToElement(el.innerHTML);
    const html = root.innerHTML;
    if (isEffectivelyEmpty(html)) { setFormError(tx('templates.contentEmpty')); return; }
    if (html.length > MAX_CONTENT_CHARS) {
      setFormError(tx('templates.contentTooLong', { count: html.length, max: MAX_CONTENT_CHARS }));
      return;
    }
    if (removed > 0) {
      el.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
    }
    save.mutate({
      name: trimmedName,
      description: trimmedDescription,
      language,
      contentHtml: html,
      frameworkIds,
      ...(id ? { isActive } : {}),
    });
  };

  const toggleFramework = (fid: string) =>
    setFrameworkIds((cur) => (cur.includes(fid) ? cur.filter((x) => x !== fid) : [...cur, fid]));

  const loading = id !== null && !hydrated;

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="min-w-0">
        <button type="button" className="text-xs font-medium text-brand hover:underline" onClick={onCancel}>
          ← {tx('templates.back')}
        </button>
        <h1 className="oc-h1 mt-1">{id ? tx('templates.editTitle') : tx('templates.newTitle')}</h1>
      </header>

      {existing.isError ? (
        <ErrorNotice message={tx('templates.loadFailed')} onRetry={() => void existing.refetch()} />
      ) : loading ? (
        <div role="status" aria-label={tx('templates.loadingOne')}><Skeleton className="h-64" /></div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4">
          <Card className="space-y-1">
            <Field label={tx('templates.fieldName')} htmlFor="ct-name" required>
              <input
                id="ct-name" className="oc-input" value={name} maxLength={255}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label={tx('templates.fieldDescription')} htmlFor="ct-desc" required>
              <textarea
                id="ct-desc" className="oc-input" rows={2} value={description} maxLength={1000}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Field label={tx('templates.fieldLanguage')} hint={tx('templates.fieldLanguageHint')} htmlFor="ct-lang">
              <select
                id="ct-lang" className="oc-input sm:!w-auto" value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {LANGUAGES.map((code) => (
                  <option key={code} value={code}>{languageName(code)} ({code})</option>
                ))}
              </select>
            </Field>

            <fieldset className="mb-4">
              <legend className="oc-label">{tx('templates.fieldFrameworks')}</legend>
              <p className="text-xs text-ink-muted mb-2">{tx('templates.fieldFrameworksHint')}</p>
              {frameworks.isLoading ? (
                <Skeleton className="h-8" />
              ) : options.length === 0 ? (
                <p className="oc-muted">{tx('templates.noLicensedFrameworks')}</p>
              ) : (
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {options.map((f) => (
                    <label key={f.id} className="inline-flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={frameworkIds.includes(f.id)}
                        onChange={() => toggleFramework(f.id)}
                      />
                      {f.name}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            {id && (
              <label className="inline-flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                {tx('templates.fieldActive')}
              </label>
            )}
          </Card>

          <Card className="space-y-3">
            <div>
              <span className="oc-label">{tx('templates.fieldContent')}</span>
              <p className="text-xs text-ink-muted">{tx('templates.fieldContentHint')}</p>
            </div>
            {/* Same component, toolbar and clean-up rules as PolicyEditor. */}
            <RichTextEditor
              initialHtml={existing.data?.contentHtml ?? ''}
              editorRef={editorRef}
              onStatus={setStatus}
              tp={tp}
              disabled={save.isPending}
            />
            <p className="text-xs text-ink-muted leading-relaxed">{tp('policies.supportedNote')}</p>
          </Card>

          {formError && <ErrorNotice message={formError} />}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? tx('templates.saving') : tx('templates.save')}
            </Button>
            <Button type="button" onClick={onCancel} disabled={save.isPending}>{tx('templates.cancel')}</Button>
          </div>
        </form>
      )}
    </div>
  );
}
